import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Compass, BookOpen, ExternalLink } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { VENTURES } from "../../constants";

const VENTURE_META = {
  AdmissionsNest: {
    icon: GraduationCap,
    gradient: "from-[#0B5D4B] to-[#128C7E]",
    badgeBg: "bg-emerald-50 text-emerald-800 border-emerald-200",
    hoverBorder: "hover:border-[#0B5D4B]/40 hover:shadow-[#0B5D4B]/15",
    accentColor: "#0B5D4B",
    tags: ["India & Abroad", "SOP Strategy", "Scholarships"],
  },
  CounselNest: {
    icon: Compass,
    gradient: "from-[#C89B3C] to-[#E5B558]",
    badgeBg: "bg-amber-50 text-amber-800 border-amber-200",
    hoverBorder: "hover:border-[#C89B3C]/40 hover:shadow-[#C89B3C]/15",
    accentColor: "#C89B3C",
    tags: ["Psychometrics", "DMIT Testing", "Stream Choice"],
  },
  TutorsNest: {
    icon: BookOpen,
    gradient: "from-[#4F46E5] to-[#6366F1]",
    badgeBg: "bg-indigo-50 text-indigo-800 border-indigo-200",
    hoverBorder: "hover:border-[#4F46E5]/40 hover:shadow-[#4F46E5]/15",
    accentColor: "#4F46E5",
    tags: ["1-on-1 Classes", "JEE / NEET / Boards", "Top Tutors"],
  },
};

function VentureCard({ venture, index }) {
  const meta = VENTURE_META[venture.name] || VENTURE_META.AdmissionsNest;
  const Icon = meta.icon;
  const Wrapper = venture.live ? "a" : "div";
  const wrapperProps = venture.live
    ? { href: venture.url, target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12, duration: 0.55 }}
    >
      <Wrapper
        {...wrapperProps}
        className="block h-full cursor-pointer"
        data-testid={`venture-${venture.name.toLowerCase()}`}
      >
        <Card className={`group relative h-full overflow-hidden rounded-[2rem] border border-white/80 bg-white/80 shadow-[0_10px_35px_rgba(0,0,0,0.04)] backdrop-blur-xl transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-2xl ${meta.hoverBorder}`}>
          
          {/* Image Banner with Gradient Overlay */}
          <div className="relative h-48 w-full overflow-hidden">
            <img
              src={venture.image}
              alt={venture.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#063B30]/70 via-[#063B30]/20 to-transparent transition-opacity duration-500 group-hover:opacity-85" />
            
            {/* Venture Icon Pill */}
            <div className={`absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[${meta.accentColor}] shadow-lg backdrop-blur transition-all duration-500 group-hover:scale-110 group-hover:bg-[#063B30] group-hover:text-white`}>
              <Icon size={22} />
            </div>

            {/* Status / Live Badge */}
            {venture.live ? (
              <span className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#063B30] shadow-sm backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Live Platform
              </span>
            ) : (
              <Badge className="absolute right-5 top-5 bg-[#C89B3C] text-white">
                Launching Soon
              </Badge>
            )}

            {/* Category Subtitle on Image */}
            <div className="absolute bottom-4 left-5 right-5">
              <span className="text-xs font-bold uppercase tracking-wider text-[#C89B3C]">
                {venture.label}
              </span>
              <h3 className="text-2xl font-bold text-white drop-shadow-sm">
                {venture.name}
              </h3>
            </div>
          </div>

          {/* Card Content & Feature Tags */}
          <CardContent className="p-6">
            <p className="mb-4 text-sm leading-relaxed text-gray-600 line-clamp-3">
              {venture.desc}
            </p>

            {/* Feature Pills */}
            <div className="mb-5 flex flex-wrap gap-1.5">
              {meta.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-black/[0.06] bg-black/[0.02] px-2.5 py-0.5 text-[11px] font-medium text-gray-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Interactive Launch Link */}
            <div className="flex items-center justify-between border-t border-black/5 pt-4">
              <span className="inline-flex items-center text-sm font-bold text-[#0B5D4B] transition-colors duration-300 group-hover:text-[#063B30]">
                {venture.live ? "Explore Platform" : "Coming Soon"}
                <ArrowRight className="ml-1.5 transition-transform duration-300 ease-out group-hover:translate-x-1.5" size={16} />
              </span>
              {venture.live && (
                <span className="text-gray-400 group-hover:text-[#0B5D4B] transition-colors">
                  <ExternalLink size={15} />
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </Wrapper>
    </motion.div>
  );
}

export default function VenturesSection() {
  return (
    <section id="ventures" className="relative z-10 px-4 py-12 sm:px-6 md:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/30 bg-amber-50/80 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#916A1E]">
              Connected Platforms
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-[#063B30] sm:text-4xl md:text-5xl">
              Three Specialized Verticals.
            </h2>
          </div>
          <p className="max-w-md text-sm sm:text-base text-gray-600 leading-relaxed">
            Each venture delivers dedicated domain depth, while Crescet Edustreams connects
            your data and guidance seamlessly through AskPurple.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {VENTURES.map((v, i) => (
            <VentureCard key={v.name} venture={v} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
